import { AppRedirect, UserType } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import App from '@modules/husleie-framework/src/static-classes/App.class.js';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { AppRedirectData } from '@modules/husleie-framework/src/types/AppRedirectData.entity';
import { SessionType } from '@modules/husleie-framework/src/types/Session.entity';
import { LandlordType, LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import { paramCase } from 'change-case';
import { stringify } from 'querystring';
import request from 'superagent';

export default class RedirectOperations {
  private static prefix = 'https://';

  private static getQueryParams(paramsObject: Record<string, any>): string {
    const valuedQueryParamsObject: Record<string, string> = JSON.parse(JSON.stringify(paramsObject));
    return Object.keys(valuedQueryParamsObject).length === 0 ? '' : `?${stringify(valuedQueryParamsObject)}`;
  }

  static goToURL(appRedirect: AppRedirect, redirectData: AppRedirectData = {}, newTab = false): void {
    const url: string = RedirectOperations.getURL(appRedirect, redirectData);
    if (newTab) {
      window.open(url);
    } else {
      window.location.href = url;
    }
  }

  static getURL(appRedirect: AppRedirect, redirectData: AppRedirectData = {}): string {
    switch (appRedirect) {
      case AppRedirect.SIGNUP:
        return RedirectOperations.getSignUpURL();
      case AppRedirect.LOGIN:
        return RedirectOperations.getLogInURL(redirectData.redirectURL!, redirectData.token);
      case AppRedirect.LOGOUT:
        return RedirectOperations.getLogOutURL(redirectData.isSudo!);
      case AppRedirect.DASHBOARD_SUDO:
        return RedirectOperations.getDashboardSudoURL();
      case AppRedirect.DASHBOARD_MYPAGE:
        return RedirectOperations.getDashboardLandlordsURL(redirectData.fromSignup);
      case AppRedirect.DASHBOARD_LANDLORD:
        return RedirectOperations.getDashboardLandlordURL(redirectData.landlordId!);
      case AppRedirect.DASHBOARD_RENTAL_OBJECT_CREATE:
        return RedirectOperations.getRentalObjectCreationURL();
      case AppRedirect.CONTRACT_CREATE:
        return RedirectOperations.getContractCreationURL(
          redirectData.rentalObjectId,
          redirectData.tenancyId,
          redirectData.candidateId,
          redirectData.fromSignup
        );
      case AppRedirect.CONTRACT_EXISTING_CREATE:
        return RedirectOperations.getContractExistingCreationURL(redirectData.rentalObjectId);
      case AppRedirect.CONTRACT_EDIT:
        return RedirectOperations.getContractEditURL(redirectData.uuid!);
      case AppRedirect.CONTRACT_PREVIEW:
        return RedirectOperations.getContractPreviewURL(redirectData.userType!, redirectData.uuid!);
      case AppRedirect.CONTRACT_SIGN:
        return RedirectOperations.getContractSigningURL(redirectData.uuid!, redirectData.userType!);
      case AppRedirect.INSPECTION_REPORT_CREATE:
        return RedirectOperations.getInspectionReportCreationURL(
          redirectData.landlordId,
          redirectData.rentalObjectId,
          redirectData.contractId,
          redirectData.tenancyId,
          redirectData.fromSignup,
          redirectData.isLegacy
        );
      case AppRedirect.INSPECTION_REPORT_PREVIEW:
        return RedirectOperations.getInspectionReportPreviewURL(redirectData.userType!, redirectData.uuid!);
      case AppRedirect.INSPECTION_REPORT_EDIT:
        return RedirectOperations.getInspectionReportEditURL(
          redirectData.inspectionReportType!,
          redirectData.uuid!,
          redirectData.landlordId,
          redirectData.isLegacy
        );
      case AppRedirect.INSPECTION_REPORT_SIGN:
        return RedirectOperations.getInspectionReportSigningURL(
          redirectData.uuid!,
          redirectData.signerUuid!,
          redirectData.userType!
        );
      case AppRedirect.AD_CREATE:
        return RedirectOperations.getAdCreationURL(
          redirectData.landlordId!,
          redirectData.rentalObjectId!,
          redirectData.shouldCopyFromPreviousAd,
        );
      case AppRedirect.AD_EDIT:
        return RedirectOperations.getAdEditURL(
          redirectData.landlordId!,
          redirectData.adId!,
        );
      case AppRedirect.TENANCY_TERMINATION:
        return RedirectOperations.getTenancyTerminationURL(redirectData.rentalObjectId, redirectData.tenancyId);
      case AppRedirect.DEPOSIT_ACCOUNT:
        return RedirectOperations.getDepositAccountCreationURL(
          redirectData.landlordId,
          redirectData.landlordName,
          redirectData.landlordType,
          redirectData.rentalObjectId,
          redirectData.tenancyId,
          redirectData.companyName,
          redirectData.companyOrgNumber,
          redirectData.fromSignup,
          redirectData.tenantName,
          redirectData.tenantEmail,
          redirectData.tenantCellphone,
          redirectData.rentRent,
          redirectData.rentDuration,
          redirectData.rentStartdate,
          redirectData.rentEnddate
        );
      case AppRedirect.DEPOSIT_ACCOUNT_SIGN:
        return RedirectOperations.getDepositAccountSignURL(redirectData.uuid!);
      case AppRedirect.LOCATION_REDIRECT:
        return RedirectOperations.getWindowLocationRedirectURL();
      case AppRedirect.DASHBOARD_LANDLORD_TENANCY_DETAILS:
        return RedirectOperations.getDashboardTenancyDetails(
          redirectData.landlordId!,
          redirectData.rentalObjectId!,
          redirectData.tenancyId!
        );
      case AppRedirect.DASHBOARD_LANDLORD_TENANCY_PAYMENTS_DETAILS:
        return RedirectOperations.getDashboardTenancyPaymentsDetails(
          redirectData.landlordId!,
          redirectData.rentalObjectId!,
          redirectData.tenancyId!
        );
      case AppRedirect.BANKID_VERIFICATION:
        return RedirectOperations.getBankIDVerificationURL(redirectData.origin, redirectData.redirectURL);
      case AppRedirect.PROSPECT_DETAILS:
        return RedirectOperations.getProspectDetailsURL(
          redirectData.landlordId,
          redirectData.rentalObjectId,
          redirectData.iMId,
          redirectData.prospectId,
        );
      default:
        throw new Error('Invalid app redirect');
    }
  }

  private static getSignUpURL(): string {
    return this.prefix + location.hostname + (App.isDevelopment() ? ':8010' : '') + '/app/signup/';
  }

  private static getLogInURL(redirect: undefined | null | string, token: undefined | null | string = null): string {
    const queryParamsObject: Record<string, undefined | string> = {
      redirect: redirect ?? undefined,
      inputEmail: token ? '__jwt_bearer' : undefined,
      inputPassword: token ?? undefined,
    };
    const valuedQueryParamsObject: Record<string, string> = JSON.parse(JSON.stringify(queryParamsObject));
    const queryParams: null | string =
      Object.keys(valuedQueryParamsObject).length > 0 ? stringify(valuedQueryParamsObject) : null;
    const loginURL: string = App.getLoginURL();
    let returnURL: string;

    if (!loginURL.startsWith('https')) {
      returnURL =
        this.prefix +
        location.hostname +
        (App.isDevelopment() ? ':8080' : '') +
        loginURL +
        (queryParams ? `?${queryParams}` : '');
    } else {
      returnURL = loginURL + (queryParams ? `?${queryParams}` : '');
    }

    return returnURL;
  }

  private static getLogOutURL(isSudo: null | boolean): string {
    return isSudo ? '/login/exit_sudo' : '/logout';
  }

  private static getDashboardSudoURL(): string {
    return this.prefix + location.hostname + (App.isDevelopment() ? ':8040' : '') + '/dashboard/administrator/';
  }

  private static getDashboardLandlordsURL(fromSignup = false): string {
    if (!fromSignup) {
      return this.prefix + location.hostname + (App.isDevelopment() ? ':8040' : '') + '/dashboard/landlord/mypage/';
    }
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8040' : '') +
      `/dashboard/landlord/mypage?from_signup=${fromSignup}`
    );
  }

  private static getDashboardLandlordURL(landlordId: number | string): string {
    return (
      this.prefix + location.hostname + (App.isDevelopment() ? ':8040' : '') + `/dashboard/landlord/${landlordId}/`
    );
  }

  private static getDashboardTenancyDetails(
    landlordId: number | string,
    rentalObjectId: number | string,
    tenancyId: number | string
  ): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8040' : '') +
      `/dashboard/landlord/${landlordId}/rentalObject/${rentalObjectId}/tenancy/${tenancyId}/tenancy/`
    );
  }
  private static getDashboardTenancyPaymentsDetails(
    landlordId: number | string,
    rentalObjectId: number | string,
    tenancyId: number | string
  ): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8040' : '') +
      `/dashboard/landlord/${landlordId}/rentalObject/${rentalObjectId}/tenancy/${tenancyId}/payments/`
    );
  }

  private static getWindowLocationRedirectURL(): string {
    const params = new URLSearchParams(window.location.search);
    const redirect: null | string = params.get('redirect');
    return redirect ? decodeURIComponent(redirect) : RedirectOperations.getDashboardLandlordsURL();
  }

  private static getContractCreationURL(
    rentalObjectId: undefined | number | string,
    tenancyId: undefined | number | string,
    candidateId: undefined | number,
    fromSignup = false
  ): string {
    const params: Record<string, any> = {
      from_signup: fromSignup ? true : undefined,
      rental_object_id: rentalObjectId,
      tenancy_id: tenancyId,
      candidate_id: candidateId,
    };

    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8020' : '') +
      '/app/contract/' +
      this.getQueryParams(params)
    );
  }

  private static getContractExistingCreationURL(rentalObjectId: undefined | number | string): string {
    const param = `rental_object_id=${rentalObjectId}`;

    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8055' : '') +
      `/app/create-tenancy/?${param}`
    );
  }

  private static getContractEditURL(uuid: string): string {
    return (
      this.prefix + location.hostname + (App.isDevelopment() ? ':8020' : '') + '/app/contract/' + uuid + '/tenant/'
    );
  }

  private static getContractPreviewURL(userType: UserType, contractSessionUuid: string): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8100' : '') +
      `/app/download/contract/${contractSessionUuid}/${userType}/preview`
    );
  }

  private static getRentalObjectCreationURL(): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8040' : '') +
      '/dashboard/landlord/create-rental-object/'
    );
  }

  /**
   *
   * @param uuid contract uuid
   * @param userType landlord | tenant
   * @returns
   */
  private static getContractSigningURL(uuid: string, userType: UserType): string {
    return `${
      this.prefix + location.hostname + (App.isDevelopment() ? ':8030' : '')
    }/app/signing/contract/${uuid}/${userType}`;
  }

  private static getTenancyTerminationURL(
    rentalObjectId: undefined | number | string,
    tenancyId: undefined | number | string
  ): string {
    const params: Record<string, any> = {
      rental_object_id: rentalObjectId ?? undefined,
      tenancy_id: tenancyId ?? undefined,
    };

    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8021' : '') +
      '/app/leaseTermination/' +
      this.getQueryParams(params)
    );
  }

  private static getInspectionReportCreationURL(
    landlordId: undefined | number | string,
    rentalObjectId: undefined | number | string,
    contractId: undefined | number | string,
    tenancyId: undefined | number | string,
    fromSignup = false,
    isLegacy = false
  ): string {
    if (isLegacy) {
      const url = `${this.prefix + location.hostname}${App.isDevelopment() ? ':8040' : ''}/vintageboard/landlord/${landlordId}/prefill/top`;
      if (tenancyId) {
        return `${url}?origin=tenancy&id=${tenancyId}`;
      } else if (rentalObjectId) {
        return `${url}?origin=rental_object&id=${rentalObjectId}`;
      } else {
        return url;
      }
    }

    const params: Record<string, any> = {
      from_signup: fromSignup ? true : undefined,
      rental_object_id: rentalObjectId ?? undefined,
      contract_id: contractId ?? undefined,
      tenancy_id: tenancyId ?? undefined,
    };

    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8090' : '') +
      '/app/inspection-report/' +
      this.getQueryParams(params)
    );
  }

  private static getInspectionReportPreviewURL(userType: UserType, sessionUuid: string): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8100' : '') +
      `/app/download/inspection-report/${sessionUuid}/${userType}/preview`
    );
  }

  private static getInspectionReportEditURL(
    type: SessionType,
    uuid: string,
    landlordId?: string | number,
    isLegacy = false
  ): string {
    if (isLegacy) {
      return `${App.isProduction() ? this.prefix + location.hostname : App.getHostURL()}/dashboard/landlord/${landlordId}/top/${uuid}/step/2`;
    }
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8090' : '') +
      `/app/inspection-report/${paramCase(type)}/${uuid}/landlord`
    );
  }

  private static getInspectionReportSigningURL(uuid: string, signerUuid: string, userType: UserType): string {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8030' : '') +
      `/app/signing/inspection-report/${uuid}/${userType}/${signerUuid}`
    );
  }

  private static getAdCreationURL(
    landlordId: number | string,
    rentalObjectId: number | string,
    shouldCopyFromPreviousAd?: boolean,
  ) {
    const url = new URL(
      `/app/pubomatic/${landlordId}`,
      `${this.prefix}/${location.hostname}${App.isDevelopment() ? ':8022' : ''}`,
    );
    url.searchParams.append('rental_object_id', String(rentalObjectId));

    if (shouldCopyFromPreviousAd) {
      url.searchParams.append('copy', 'true');
    }

    return url.toString();
  }

  private static getAdEditURL(
    landlordId: number | string,
    adId: number | string,
  ) {
    const url = new URL(
      `/app/pubomatic/${landlordId}/${adId}`,
      `${this.prefix}/${location.hostname}${App.isDevelopment() ? ':8022' : ''}`,
    );
    url.searchParams.append('skipIntroPage', 'true');

    return url.toString();
  }

  private static getDepositAccountCreationURL(
    landlordId: undefined | number | string,
    landlordName: undefined | string,
    landlordType: undefined | LandlordType,
    rentalObjectId: undefined | number | string,
    tenancyId: undefined | number | string,
    companyName: undefined | string,
    companyOrgNumber: undefined | string,
    fromSignup = false,
    tenantName: undefined | string,
    tenantEmail: undefined | string,
    tenantCellphone: undefined | string,
    rentRent: undefined | number,
    rentDuration: undefined | string,
    rentStartdate: undefined | string,
    rentEnddate: undefined | string,
  ): string {
    const params: Record<string, any> = {
      from_signup: fromSignup ? true : undefined,
      landlord_id: landlordId ?? undefined,
      landlord_type: landlordType === LandlordTypeEnum.PROFESSIONAL ? 'company' : 'private',
      landlord_name: landlordName,
      company_name: landlordType === LandlordTypeEnum.PROFESSIONAL ? companyName : undefined,
      company_orgNumber: landlordType === LandlordTypeEnum.PROFESSIONAL ? companyOrgNumber : undefined,
      rental_object_id: rentalObjectId ?? undefined,
      tenancy_id: tenancyId ?? undefined,
      tenant_name: tenantName ?? undefined,
      tenant_email: tenantEmail ?? undefined,
      tenant_cellphone: tenantCellphone ?? undefined,
      rent_rent: rentRent ?? undefined,
      rent_duration: rentDuration ?? undefined,
      rent_startdate: rentStartdate ?? undefined,
      rent_enddate: rentEnddate ?? undefined,
    };

    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8023' : '') +
      '/app/depAccount/' +
      this.getQueryParams(params)
    );
  }

  private static getDepositAccountSignURL(uuid: string): string {
    return this.prefix + location.hostname + (App.isDevelopment() ? ':8060' : '') + `/app/depAccountSigning/${uuid}`;
  }

  private static getBankIDVerificationURL(origin?: string, redirectURL?: string | null): string {
    const url = new URL(
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8061' : '') +
      '/app/generic-app/bankid-verification/'
    );

    if (origin) {
      url.searchParams.append('origin', origin);
    }
    if (redirectURL) {
      url.searchParams.append('redirect', redirectURL);
    }
    return url.toString();
  }

  private static getProspectDetailsURL(
    landlordId?: string | number,
    rentalObjectId?: string | number,
    iMId?: string,
    prospectId?: string
  ) {
    return (
      this.prefix +
      location.hostname +
      (App.isDevelopment() ? ':8040' : '') +
      `/dashboard/landlord/${landlordId}/rentalObject/${rentalObjectId}/ads/iM/${iMId}/prospect/${prospectId}`
    );
  }

  static async forceAuthentication(): Promise<void> {
    if (!App.isMock() && !App.isMockedAPI()) {
      const pingHost: string = App.getAPIPingHost() as string;

      try {
        const response = await request.post(pingHost).withCredentials().set('X-Ping', 'pong');
        if (response.body.logged_in === 0) {
          this.goToURL(AppRedirect.LOGIN, { redirectURL: window.location.href });
        }
      } catch (error) {
        LogOperations.error('API|forceAuthentication', 'Error pinging server.');
      }
    }
  }

  static async forceDashboardIfLoggedIn(): Promise<void> {
    if (!App.isMock() && !App.isMockedAPI()) {
      const pingHost: string = App.getAPIPingHost() as string;

      try {
        const response = await request.post(pingHost).withCredentials().set('X-Ping', 'pong');
        if (response.body.logged_in === 1) {
          window.location.href = RedirectOperations.getDashboardLandlordsURL();
        }
      } catch (error) {
        LogOperations.error('API|forceDashboardIfLoggedIn', 'Error pinging server.');
      }
    }
  }
}
